import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KPI } from "@/components/Primitives";
import { toast } from "sonner";
import { RefreshCw, Building2, Cloud, ShieldCheck } from "lucide-react";

export default function Identity() {
  const [entra, setEntra] = useState(null);
  const [ad, setAd] = useState(null);
  const [m365, setM365] = useState(null);
  const [entraStatus, setEntraStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [es, e, a, m] = await Promise.all([
      api.get("/integrations/entra/status"),
      api.get("/integrations/entra/security").catch(() => ({data: null})),
      api.get("/integrations/ad/analytics").catch(() => ({data: null})),
      api.get("/integrations/m365/overview").catch(() => ({data: null})),
    ]);
    setEntraStatus(es.data); setEntra(e.data); setAd(a.data); setM365(m.data);
  };
  useEffect(() => { load(); }, []);

  const syncEntra = async () => { setBusy(true); try { await api.post("/integrations/entra/sync"); toast.success("Entra ID sync complete"); await load(); } finally { setBusy(false); } };
  const syncAD = async () => { setBusy(true); try { await api.post("/integrations/ad/sync"); toast.success("AD sync complete"); await load(); } finally { setBusy(false); } };
  const syncM365 = async () => { setBusy(true); try { await api.post("/integrations/m365/sync"); toast.success("M365 sync complete"); await load(); } finally { setBusy(false); } };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Identity Governance</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Active Directory · Entra ID · Microsoft 365</h1>
        <p className="text-sm text-zinc-500 mt-1">{entraStatus?.configured ? "Microsoft Graph: LIVE" : "Microsoft Graph: SIMULATION MODE (configure GRAPH_TENANT_ID/CLIENT_ID/SECRET to go live)"}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Microsoft Entra ID" icon={Cloud} sub={entraStatus?.configured ? "Live (Microsoft Graph)" : "Simulated"} onSync={syncEntra} busy={busy} testid="entra-sync">
          {entra ? (
            <>
              <KPI label="Total Users" value={entra.total_users} testid="entra-users"/>
              <KPI label="Disabled" value={entra.disabled} tone="warn" testid="entra-disabled"/>
              <KPI label="Dormant 90d" value={entra.dormant_90d} tone="warn" testid="entra-dormant"/>
              <KPI label="Risky Users" value={entra.risky_users} tone="bad" testid="entra-risky"/>
              <KPI label="MFA Coverage" value={`${entra.mfa_coverage_pct}%`} tone="good" testid="entra-mfa"/>
              <KPI label="Identity Score" value={entra.identity_security_score} tone={entra.identity_security_score >= 80 ? "good" : "warn"} testid="entra-score"/>
            </>
          ) : <Empty label="Not synced yet"/>}
        </Card>

        <Card title="Active Directory" icon={Building2} sub="Simulated (LDAP adapter ready)" onSync={syncAD} busy={busy} testid="ad-sync">
          {ad ? (
            <>
              <KPI label="Total Users" value={ad.total_users} testid="ad-users"/>
              <KPI label="Dormant 90d" value={ad.dormant_users_90d} tone="warn" testid="ad-dormant"/>
              <KPI label="Locked" value={ad.locked_accounts} tone="warn" testid="ad-locked"/>
              <KPI label="Disabled" value={ad.disabled_accounts} tone="warn" testid="ad-disabled"/>
              <KPI label="Domain Admins" value={ad.domain_admins} tone="warn" testid="ad-domain-admins"/>
              <KPI label="Inactive Computers" value={ad.inactive_computers_90d} tone="warn" testid="ad-inactive-pcs"/>
            </>
          ) : <Empty label="Not synced yet"/>}
        </Card>

        <Card title="Microsoft 365" icon={ShieldCheck} sub="Simulated (Graph adapter ready)" onSync={syncM365} busy={busy} testid="m365-sync">
          {m365 ? (
            <>
              <KPI label="Mailboxes" value={m365.mailboxes} testid="m365-mailboxes"/>
              <KPI label="Teams" value={m365.teams} testid="m365-teams"/>
              <KPI label="SharePoint Sites" value={m365.sharepoint_sites} testid="m365-sites"/>
              {(m365.licenses || []).map(l => <KPI key={l.sku} label={l.sku} value={`${l.assigned}/${l.total}`} testid={`m365-lic-${l.sku.replaceAll(' ','-')}`}/>)}
            </>
          ) : <Empty label="Not synced yet"/>}
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, sub, children, onSync, busy, testid }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2"><Icon size={16} className="text-blue-400"/><div className="font-display font-medium">{title}</div></div>
        <button onClick={onSync} disabled={busy} data-testid={testid} className="inline-flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-2.5 py-1"><RefreshCw size={12} className={busy?"animate-spin":""}/>Sync</button>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">{sub}</div>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function Empty({ label }) { return <div className="col-span-2 text-xs text-zinc-500 py-4">{label}</div>; }
